import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Skeleton,
  Stack,
  Text,
  HStack,
} from '@chakra-ui/react';
import { toaster, useColorModeValue } from '@mono/chakra';
import { isHttpError, useHttp, useHttpQuery } from '@mono/http-hook';
import { FC, useMemo, useState } from 'react';
import { useCurrentProject } from '../projects/hooks';

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

type ProjectEnvironment = {
  id: string;
  name: string;
  key: string;
  isDefault: boolean;
  projectId: string;
  createdAt: string;
  updatedAt: string;
};

const ProjectEnvironmentsTab: FC = () => {
  const projectQuery = useCurrentProject();
  const http = useHttp();
  const cardBg = useColorModeValue('bg.surface', 'gray.900');
  const [environmentName, setEnvironmentName] = useState('');
  const [isCreatingEnvironment, setIsCreatingEnvironment] = useState(false);

  const project = projectQuery.data?.project;
  const environmentsQuery = useHttpQuery<{ environments: ProjectEnvironment[] }>(
    {
      queryKey: ['project-environments', project?.id, project?.organizationId],
      config: {
        method: 'get',
        url: project
          ? `/api/v1/organizations/${project.organizationId}/projects/${project.id}/environments`
          : '',
        withCredentials: true,
      },
      options: {
        enabled: Boolean(project?.id && project?.organizationId),
      },
    }
  );

  const canManageEnvironments = useMemo(() => {
    if (!project) return false;
    return project.role === 'ADMIN';
  }, [project]);

  const environments = environmentsQuery.data?.environments ?? [];

  const handleCreateEnvironment = async () => {
    if (!project) return;
    const trimmed = environmentName.trim();
    if (!trimmed) return;
    setIsCreatingEnvironment(true);

    try {
      await http.post(
        `/api/v1/organizations/${project.organizationId}/projects/${project.id}/environments`,
        { name: trimmed },
        { withCredentials: true }
      );

      setEnvironmentName('');
      await environmentsQuery.refetch();
      toaster.create({
        type: 'success',
        title: 'Environment added',
        description: 'The new environment is ready to use.',
      });
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to create environment right now.'
      );
      toaster.create({
        type: 'error',
        title: 'Failed to create environment',
        description: message,
      });
    } finally {
      setIsCreatingEnvironment(false);
    }
  };

  return (
    <Stack gap="6">
      <Card.Root bg={cardBg}>
        <Card.Header gap="1">
          <Card.Title>Environments</Card.Title>
          <Card.Description>
            Manage deployment targets for this project. Draft is always
            available.
          </Card.Description>
        </Card.Header>
        <Card.Body gap="4">
          {!canManageEnvironments ? (
            <Alert.Root status="info">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Admin-only</Alert.Title>
                <Alert.Description>
                  Only admins can add new environments.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}

          <Stack gap="3">
            {environmentsQuery.isLoading ? (
              <Skeleton height="4rem" borderRadius="lg" />
            ) : environmentsQuery.error ? (
              <Text color="fg.error" fontSize="sm">
                Unable to load environments right now.
              </Text>
            ) : (
              environments.map((environment) => (
                <HStack
                  key={environment.id}
                  justify="space-between"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="lg"
                  px="4"
                  py="3"
                >
                  <Stack gap="0">
                    <Text fontWeight="medium">{environment.name}</Text>
                    <Text fontSize="xs" color="fg.muted">
                      Key: {environment.key}
                    </Text>
                  </Stack>
                  {environment.isDefault ? (
                    <Badge variant="solid" colorPalette="green">
                      Default
                    </Badge>
                  ) : (
                    <Badge variant="subtle" colorPalette="gray">
                      Custom
                    </Badge>
                  )}
                </HStack>
              ))
            )}

            {canManageEnvironments ? (
              <Stack gap="2">
                <Field.Root>
                  <Field.Label>New environment</Field.Label>
                  <Input
                    placeholder="Staging"
                    value={environmentName}
                    onChange={(event) => setEnvironmentName(event.target.value)}
                    disabled={isCreatingEnvironment}
                  />
                  <Field.HelperText>
                    Use a clear label like Staging or Production.
                  </Field.HelperText>
                </Field.Root>
                <Button
                  alignSelf="flex-start"
                  onClick={handleCreateEnvironment}
                  disabled={!environmentName.trim() || isCreatingEnvironment}
                  loading={isCreatingEnvironment}
                  colorPalette="primary"
                >
                  Add environment
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

export default ProjectEnvironmentsTab;
